/**
 * Vocalist Web2App Configuration
 * Central configuration for Paddle, Statsig, content, and features
 */

import type {
  Environment,
  EnvironmentConfigs,
  Config as ConfigType,
} from '../types/config.types';

// Auto-detect environment based on hostname
const detectEnvironment = (): Environment => {
  const hostname = window.location.hostname.toLowerCase();
  // Check if hostname contains -test or -sandbox patterns
  const isSandbox =
    hostname.includes('-test') ||
    hostname.includes('-sandbox') ||
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1') ||
    window.location.href.includes('file://');
  return isSandbox ? 'sandbox' : 'production';
};

// Environment-specific configurations
const ENVIRONMENTS: EnvironmentConfigs = {
  sandbox: {
    paddle: {
      token: 'test_7b7ce7d31fbe21ca25839dabc59',
      vendorId: 38054,
    },
    statsig: {
      clientKey: 'client-32ZqbEx3TVBIrDsiien8SzXvzaKSrDAPVCvdHOUNAPz',
    },
    // Same Amplitude project key as the mobile apps (Flutter sandbox) so web + app share one
    // project — required for cross-system user matching.
    amplitude: {
      apiKey: '60f539b6f99a355e0cfefcde45f1dbb7',
    },
    appStore: {
      ios: 'com.shamruk.musicapp://main?appwrite_sandbox=true',
      mac: 'com.shamruk.musicapp://main?appwrite_sandbox=true',
      visionPro: 'com.shamruk.musicapp://main?appwrite_sandbox=true',
      android: 'https://play.google.com/store/apps/details?id=com.shamruk.musicapp',
    },
  },
  production: {
    paddle: {
      token: 'live_c50eb02a5adcd617a3ea0d386e4',
      vendorId: 242567,
    },
    statsig: {
      clientKey: 'client-Dnt5mF7prDn6OuIMwloNi9eKtE5robiSPID3TRC2TUQ',
    },
    // Same Amplitude project key as the mobile apps (Flutter production).
    amplitude: {
      apiKey: 'e5c021233acf378c011c8d4cadfaeff5',
    },
    appStore: {
      ios: 'https://apps.apple.com/app/id1616751664',
      mac: 'https://apps.apple.com/app/id1616751664',
      visionPro: 'https://apps.apple.com/app/id1616751664',
      android: 'https://play.google.com/store/apps/details?id=com.shamruk.musicapp',
    },
  },
};

// Get current environment
const CURRENT_ENV = detectEnvironment();

export const Config: ConfigType = {
  // Current environment (auto-detected)
  environment: CURRENT_ENV,

  // Paddle Configuration (merged from environment-specific settings)
  paddle: {
    // Environment: auto-detected based on hostname
    environment: CURRENT_ENV === 'sandbox' ? 'sandbox' : 'production',

    // Paddle Client Token (environment-specific)
    token: ENVIRONMENTS[CURRENT_ENV].paddle.token,

    // Your Paddle Vendor ID (environment-specific)
    vendorId: ENVIRONMENTS[CURRENT_ENV].paddle.vendorId,

    // Apple Pay express checkout strategy.
    // 'express' = Paddle official express checkout (variant:'express', launched 2026-04-27);
    // 'legacy'  = the older allowedPaymentMethods:['apple_pay','google_pay'] approach.
    // expressVariant: CURRENT_ENV === 'sandbox' ? 'express' : 'legacy',
    expressVariant: 'legacy',

    // Pricing (for display purposes)
    pricing: {
      annual: {
        original: 59.99,
        discounted: 39.99,
        discount: 33, // percentage
      },
      monthly: {
        price: 9.99,
      },
      familyAddon: 10.0,
    },
  },

  // Analytics Configuration
  analytics: {
    enabled: true,
    trackingId: '', // Legacy analytics tracking ID
    // Debug-only escape hatch: forces Statsig + Amplitude to init at startup, bypassing the consent
    // gate (for hardcore startup debugging). MUST stay false for normal use / production.
    hardcoreStartupDebug: false,
    statsig: {
      clientKey: ENVIRONMENTS[CURRENT_ENV].statsig.clientKey, // Environment-specific key
      enableAutoCapture: true, // Auto-capture clicks and page views
      enableSessionReplay: false, // Session replay disabled by default
      // User will be identified by email when captured
      anonymousBeforeEmail: true,
    },
    amplitude: {
      apiKey: ENVIRONMENTS[CURRENT_ENV].amplitude.apiKey, // Environment-specific (shared app project)
      enabled: true,
    },
    metaPixel: {
      pixelId: '847770357286611', // Meta Pixel ID
      enabled: true, // Enabled after user consent
      requireConsent: true, // GDPR-compliant consent required
    },
    apphud: {
      pixelId: '5505efd4-07a0-47b3-af54-9eca063979d4', // Apphud Pixel ID
      enabled: false, // Disabled - Apphud api-legacy.apphud.com has SSL certificate issues
      requireConsent: true, // GDPR-compliant consent required
    },
  },

  // Email Service Configuration
  emailService: {
    endpoint: '/api/subscribe', // Your email subscription endpoint
    listId: '', // Your email list ID
  },

  // Timer Configuration
  timer: {
    // Offer duration in minutes
    offerDuration: 10,
  },

  // App Store Links
  appStore: ENVIRONMENTS[CURRENT_ENV].appStore,

  // Feature Flags
  features: {
    familyPlan: true,
    limitedTimeOffer: true,
    moneyBackGuarantee: true,
    guaranteeDays: 14,
  },

  // Content Configuration
  content: {
    // Number of songs in library
    songCount: '5,000+',

    // Number of active users
    userCount: '50,000+',

    // App rating
    rating: 4.7,
    ratingCount: 298,

    // Success metrics
    metrics: {
      beginnerSuccessRate: 87, // percentage
      daysToFirstSong: 14,
    },
  },

  // A/B Test Configuration
  abTest: {
    // Stylesheet variants (3 and 4)
    // Note: These are loaded dynamically via import() in app.ts
    stylesheetVariants: {
      'palette-purple': 'styles.css', // Original purple theme
      'palette-blue': 'styles-blue.css', // New blue theme
      'palette-light': 'styles-light.css', // New light theme
      'palette-dg': 'styles-dg.css', // DG
    },
    // Default variant if none assigned
    defaultStylesheet: 'styles.css',
  },
};

export default Config;
